import type { AuthActionResponse, AuthProvider } from "@refinedev/core";

import { FhirClient } from "@/fhir";
import type { MfaMethod, UserMfaSettings } from "@/types/types";
import { GET_USER_RECORD_QUERY, REFRESH_TOKEN_MUTATION, graphqlClient } from "@/utils/GraphQLClient";
import type { GetUserInput, GetUserRecordData, RefreshTokenData } from "@/utils/GraphQLClient";

import * as Sentry from "@sentry/react";

import {
  AuthError,
  AuthErrorCode,
  TokenRefreshError,
  isRetryableError,
  isUrqlErrorRetryable,
  mapToAuthError,
} from "./auth-errors";
import {
  type AuthTokens,
  MFA_CHALLENGE_TYPES,
  clearTokens,
  getAccessToken,
  getStoredTokens,
  isTokenExpired,
  isTokenExpiringSoon,
  setStoredTokens,
} from "./token-provider";

/**
 * User role from login/refresh endpoints
 */
export interface UserRole {
  role_id: string;
  role_name: string;
  role_description: string;
  rank: number;
  scope: string | null;
}

/**
 * User data returned from login/refresh endpoints
 */
export interface UserData {
  user_id: string;
  account_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  profile_picture?: string;
  roles?: UserRole[];
  mfa?: UserMfaSettings;
  toc_completed?: boolean;
  pn_completed?: boolean;
  fhir_practitioner_id?: string;
}

/**
 * Retry configuration
 */
interface RetryConfig {
  maxRetries: number;
  baseDelayMs: number;
  maxDelayMs: number;
}

/**
 * Result from token refresh including raw response data
 */
interface TokenRefreshResult {
  tokens: AuthTokens;
  rawData: Record<string, unknown>;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
};

/**
 * Sleep for a specified duration
 */
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay with jitter
 */
const calculateBackoffDelay = (attempt: number, baseDelay: number, maxDelay: number): number => {
  // Exponential backoff: baseDelay * 2^attempt
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  // Add jitter (0-25% of delay)
  const jitter = exponentialDelay * Math.random() * 0.25;
  return Math.min(exponentialDelay + jitter, maxDelay);
};

/**
 * Auth endpoint paths
 * LOGIN, CHALLENGE, and SIGN_OUT use loginBaseUrl (practitioner/auth)
 * Token refresh uses GraphQL mutation (not REST)
 * FORGOT_PASSWORD and CONFIRM_PASSWORD use authBaseUrl
 */
const AUTH_PATHS = {
  LOGIN: "/login",
  SIGN_OUT: "/sign-out",
  // REFRESH: "/refresh",  // REMOVED - now using GraphQL mutation
  CHALLENGE: "/respond-challenge",
  FORGOT_PASSWORD: "/forgot-password",
  CONFIRM_PASSWORD: "/confirm-password",
} as const;

export interface FhirAuthConfig {
  /**
   * Base URL for authentication endpoints
   * Individual endpoint paths are hardcoded and appended to this URL
   * @example 'https://api.example.com/auth'
   */
  authBaseUrl: string;

  /**
   * Base URL for the login endpoint
   * @example 'https://api.example.com/practitioner/auth'
   */
  loginBaseUrl: string;

  /**
   * FHIR client for fetching user identity (Practitioner resource)
   */
  fhirClient: FhirClient;

  /**
   * Callback function when token changes (for FHIR client integration)
   */
  onTokenChange?: (token: string | null) => void;

  /**
   * Custom login request body transformer
   * @default (email, password) => ({ email, password })
   */
  transformLoginRequest?: (email: string, password: string) => Record<string, unknown>;

  /**
   * Custom login response transformer
   * @default (data) => data
   */
  transformLoginResponse?: (data: unknown) => AuthTokens;

  /**
   * Practitioner ID field in login response (for fetching identity)
   * @default 'practitionerId'
   */
  practitionerIdField?: string;

  /**
   * Retry configuration for token refresh
   */
  retryConfig?: Partial<RetryConfig>;
}

/**
 * Perform GraphQL token refresh with retry logic
 * Replaces REST endpoint to get correct user_id from backend
 */
const refreshTokenViaGraphQL = async (
  transformLoginResponse: (data: unknown) => AuthTokens,
  config: RetryConfig
): Promise<TokenRefreshResult> => {
  let lastError: unknown;

  for (let attempt = 0; attempt < config.maxRetries; attempt++) {
    try {
      // Call GraphQL mutation
      const result = await graphqlClient.mutation<RefreshTokenData, Record<string, never>>(REFRESH_TOKEN_MUTATION, {});

      // Check for GraphQL errors
      if (result.error) {
        const authError = new AuthError(
          `GraphQL refresh failed: ${result.error.message}`,
          AuthErrorCode.TOKEN_REFRESH_FAILED
        );

        // Check if error is retryable (network, timeout, server errors)
        if (!isUrqlErrorRetryable(result.error)) {
          throw authError;
        }

        lastError = authError;
      } else if (result.data?.refreshToken) {
        // Success - transform response to AuthTokens format
        const refreshData = result.data.refreshToken;

        // Transform using existing function (maps 'id' and 'access' to AuthTokens)
        const tokens = transformLoginResponse(refreshData);

        // Return with raw data for user_id extraction
        return {
          tokens,
          rawData: {
            user: {
              user_id: refreshData.user_id,
            },
          },
        };
      } else {
        // No data and no error - unexpected state
        throw new AuthError("Unexpected GraphQL refresh response", AuthErrorCode.TOKEN_REFRESH_FAILED);
      }
    } catch (error) {
      lastError = error;

      // Don't retry non-retryable errors
      if (error instanceof AuthError && !error.retryable) {
        throw error;
      }

      // Check if network error (retryable)
      if (!isRetryableError(error)) {
        throw mapToAuthError(error);
      }
    }

    // Wait before retrying (skip delay on last attempt)
    if (attempt < config.maxRetries - 1) {
      const delay = calculateBackoffDelay(attempt, config.baseDelayMs, config.maxDelayMs);
      console.warn(`GraphQL token refresh attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
      await sleep(delay);
    }
  }

  // All retries exhausted
  throw new TokenRefreshError(`GraphQL token refresh failed after ${config.maxRetries} attempts`, lastError);
};

/**
 * Create a FHIR-compatible authentication provider
 *
 * This auth provider integrates with any FHIR R4 backend server
 * and manages authentication state for the application.
 *
 * Features:
 * - Proactive token refresh (refreshes before expiry)
 * - Retry logic with exponential backoff for transient failures
 * - Typed error handling
 *
 * @param config - Authentication configuration
 * @returns Refine.dev AuthProvider
 *
 * @example
 * const authProvider = createFhirAuthProvider({
 *   authBaseUrl: 'https://api.example.com/auth',
 *   fhirClient,
 *   onTokenChange: (token) => {
 *     fhirClient.setTokenProvider(async () => token);
 *   }
 * });
 */
/**
 * Extended AuthProvider with custom refreshUserInfo method
 */
export interface ExtendedAuthProvider extends AuthProvider {
  refreshUserInfo: () => Promise<void>;
}

/**
 * Module-level reference to refreshUserInfo function
 * Allows hooks to access the refresh function without direct auth provider access
 */
let moduleRefreshUserInfo: (() => Promise<void>) | null = null;

/**
 * Get the module-level refreshUserInfo function
 * Used by hooks to trigger user info refresh
 */
export const getRefreshUserInfo = (): (() => Promise<void>) => {
  if (!moduleRefreshUserInfo) {
    throw new Error("Auth provider not initialized - refreshUserInfo not available");
  }
  return moduleRefreshUserInfo;
};

/**
 * Module-level reference to handle401Error function
 * Allows FhirClient to trigger auth error handling (token refresh or logout)
 */
let moduleHandle401Error: (() => Promise<{ logout: boolean; redirectTo?: string }>) | null = null;

/**
 * Get the module-level handle401Error function
 * Used by FhirClient to handle 401 errors
 */
export const getHandle401Error = (): (() => Promise<{ logout: boolean; redirectTo?: string }>) => {
  if (!moduleHandle401Error) {
    const error = new Error("Auth provider not initialized - handle401Error not available");
    Sentry.captureException(error, {
      level: "error",
      tags: { component: "auth-provider", operation: "get-handle-401-error" },
    });
    throw error;
  }
  return moduleHandle401Error;
};

export const createFhirAuthProvider = (config: FhirAuthConfig): ExtendedAuthProvider => {
  const {
    authBaseUrl,
    loginBaseUrl,
    fhirClient: _fhirClient,
    onTokenChange,
    transformLoginRequest = (email, password) => ({ email, password }),
    transformLoginResponse = data => data as AuthTokens,
    practitionerIdField: _practitionerIdField = "practitionerId",
    retryConfig,
  } = config;

  const finalRetryConfig: RetryConfig = {
    ...DEFAULT_RETRY_CONFIG,
    ...retryConfig,
  };

  // In-memory cache for user data (cleared on page refresh, restored via /refresh endpoint)
  let cachedUserData: UserData | null = null;

  /**
   * Internal function to perform token refresh
   * Uses GraphQL mutation to get correct user_id from backend
   * Deduplicates concurrent calls by reusing an in-flight promise
   */
  let inflightRefresh: Promise<AuthTokens | null> | null = null;

  const performTokenRefresh = async (): Promise<AuthTokens | null> => {
    if (inflightRefresh) {
      return inflightRefresh;
    }

    inflightRefresh = (async () => {
      try {
        const result = await refreshTokenViaGraphQL(transformLoginResponse, finalRetryConfig);

        setStoredTokens(result.tokens);

        if (result.rawData.user) {
          cachedUserData = result.rawData.user as UserData;
        }

        if (onTokenChange) {
          onTokenChange(result.tokens.idToken ?? result.tokens.accessToken);
        }

        return result.tokens;
      } catch (error) {
        console.error("Token refresh failed:", error);
        return null;
      } finally {
        inflightRefresh = null;
      }
    })();

    return inflightRefresh;
  };

  /**
   * Common handler for successful authentication - stores tokens and loads complete user profile
   */
  const handleSuccessfulAuth = async (data: Record<string, unknown>, tokens: AuthTokens): Promise<void> => {
    setStoredTokens(tokens);

    if (onTokenChange) {
      onTokenChange(tokens.idToken ?? tokens.accessToken);
    }

    // Cache basic user data from response
    if (data.user) {
      cachedUserData = data.user as UserData;
    } else {
      console.error("There was no user data.");
    }

    // Load complete user profile for consistency
    try {
      if (cachedUserData?.user_id) {
        await refreshUserInfo();
      }
    } catch (error) {
      console.warn("Failed to load user info after login:", error);
      // Continue - we have data from login response
    }
  };

  /**
   * Helper for simple POST requests that return success/error responses
   */
  const postAuthRequest = async (
    path: string,
    body: object,
    successRedirect: string
  ): Promise<{ success: true; redirectTo: string } | { success: false; error: { name: string; message: string } }> => {
    try {
      const response = await fetch(`${authBaseUrl}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const authError = mapToAuthError(errorData);
        return { success: false, error: { name: "", message: authError.message } };
      }

      return { success: true, redirectTo: successRedirect };
    } catch (error) {
      const authError = mapToAuthError(error);
      console.error(`${path} error:`, authError);
      return { success: false, error: { name: "", message: authError.message } };
    }
  };

  /**
   * Build challenge responses based on challenge type
   */
  const buildChallengeResponses = (
    challengeName: string,
    username: string,
    responseValue: string
  ): Record<string, string> => {
    const responses: Record<string, string> = { USERNAME: username };

    switch (challengeName) {
      case MFA_CHALLENGE_TYPES.NEW_PASSWORD_REQUIRED:
        responses.NEW_PASSWORD = responseValue;
        break;
      case MFA_CHALLENGE_TYPES.SMS_MFA:
        responses.SMS_MFA_CODE = responseValue;
        break;
      case MFA_CHALLENGE_TYPES.SOFTWARE_TOKEN_MFA:
        responses.SOFTWARE_TOKEN_MFA_CODE = responseValue;
        break;
      case MFA_CHALLENGE_TYPES.EMAIL_OTP:
        responses.EMAIL_OTP_CODE = responseValue;
        break;
      default:
        // Handle other challenge types as needed.
        console.warn(`Unhandled challenge type: ${challengeName}`);
        break;
    }

    return responses;
  };

  /**
   * Route to the appropriate page based on the challenge type returned by the backend.
   * Returns an auth result if a challenge was found, or null if no challenge is present.
   */
  const handleChallengeRouting = (
    data: Record<string, unknown>,
    email: string,
    redirectTo?: string
  ): AuthActionResponse | null => {
    const challenge = data.challenge as { name: string; parameters?: Record<string, string> } | undefined;
    if (!challenge?.name) {
      return null;
    }

    if (challenge.name === MFA_CHALLENGE_TYPES.NEW_PASSWORD_REQUIRED) {
      const params = new URLSearchParams({
        challengeType: challenge.name,
        email,
      });
      if (redirectTo) {
        params.set("to", redirectTo);
      }
      return {
        success: true,
        redirectTo: `/setup-new-password?${params.toString()}`,
      };
    }

    if (challenge.name === MFA_CHALLENGE_TYPES.MFA_SETUP) {
      const tokens = transformLoginResponse(data);
      setStoredTokens(tokens);
      if (onTokenChange) {
        onTokenChange(tokens.idToken ?? tokens.accessToken);
      }
      // Cache user data so refreshUserInfo works after MFA setup completes
      if (data.user) {
        cachedUserData = data.user as UserData;
      }
      const mfasCanSetup = challenge.parameters?.MFAS_CAN_SETUP || '["SMS_MFA","SOFTWARE_TOKEN_MFA"]';
      const params = new URLSearchParams({
        MFAS_CAN_SETUP: mfasCanSetup,
        email,
      });
      if (redirectTo) {
        params.set("to", redirectTo);
      }
      return {
        success: true,
        redirectTo: `/setup-mfa?${params.toString()}`,
      };
    }

    if (
      challenge.name === MFA_CHALLENGE_TYPES.SMS_MFA ||
      challenge.name === MFA_CHALLENGE_TYPES.SOFTWARE_TOKEN_MFA ||
      challenge.name === MFA_CHALLENGE_TYPES.EMAIL_OTP
    ) {
      const params = new URLSearchParams({
        challengeType: challenge.name,
        email,
      });
      if (redirectTo) {
        params.set("to", redirectTo);
      }
      // Pass email destination for EMAIL_OTP challenges
      if (challenge.name === MFA_CHALLENGE_TYPES.EMAIL_OTP && challenge.parameters?.CODE_DELIVERY_DESTINATION) {
        params.set("emailDestination", challenge.parameters.CODE_DELIVERY_DESTINATION);
      }
      return {
        success: true,
        redirectTo: `/verify-mfa?${params.toString()}`,
      };
    }

    return {
      success: false,
      error: {
        name: "ChallengeRequired",
        message: `Authentication challenge required: ${challenge.name}`,
      },
    };
  };

  /**
   * Handle challenge response (e.g., NEW_PASSWORD_REQUIRED, SMS_MFA, SOFTWARE_TOKEN_MFA)
   */
  const handleChallengeResponse = async (
    challengeName: string,
    username: string,
    responseValue: string,
    redirectTo?: string
  ) => {
    const challengeResponses = buildChallengeResponses(challengeName, username, responseValue);

    const response = await fetch(`${loginBaseUrl}${AUTH_PATHS.CHALLENGE}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        challengeName,
        challengeResponses,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const authError = mapToAuthError(errorData);
      return {
        success: false,
        error: {
          name: "",
          message: authError.message,
        },
        errorCode: authError.code,
      };
    }

    const data = await response.json();

    const challengeResult = handleChallengeRouting(data, username, redirectTo);
    if (challengeResult) {
      return challengeResult;
    }

    const tokens = transformLoginResponse(data);

    await handleSuccessfulAuth(data, tokens);

    return {
      success: true,
      redirectTo: redirectTo || "/",
    };
  };

  /**
   * Fetches and caches full user record including MFA settings
   * Automatically called after login. Manually call after profile updates.
   */
  const refreshUserInfo = async (): Promise<void> => {
    const tokens = getStoredTokens();
    if (!tokens) {
      throw new Error("Not authenticated - cannot refresh user info");
    }

    if (!cachedUserData?.user_id) {
      console.warn("No cached user ID - skipping user info refresh");
      return;
    }

    try {
      const result = await graphqlClient.query<GetUserRecordData, { input: GetUserInput }>(
        GET_USER_RECORD_QUERY,
        { input: { user_id: cachedUserData.user_id } },
        { requestPolicy: "network-only" }
      );

      if (result.error) {
        console.error("Failed to refresh user info:", result.error.graphQLErrors?.[0]?.message ?? result.error);
        throw new Error(
          `Failed to refresh user info: ${result.error.graphQLErrors?.[0]?.message ?? result.error.message}`
        );
      }

      if (result.data?.getUserRecord) {
        const userData = result.data.getUserRecord;
        cachedUserData = {
          user_id: userData.user_id,
          account_id: userData.account_id,
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email,
          profile_picture: userData.profile_picture,
          roles: userData.roles,
          mfa: userData.mfa
            ? {
                phone_number: userData.mfa.phone_number,
                phoneVerified: userData.mfa.phoneVerified ?? false,
                smsEnabled: userData.mfa.smsEnabled,
                status: userData.mfa.status as MfaMethod,
                totpEnabled: userData.mfa.totpEnabled,
                emailEnabled: userData.mfa.emailEnabled ?? false,
              }
            : undefined,
          toc_completed: userData.toc_completed,
          pn_completed: userData.pn_completed,
          fhir_practitioner_id: userData.vitall_practitioner?.fhir_practitioner_id,
        };
      }
    } catch (error) {
      console.error("Error refreshing user info:", error);
      throw error;
    }
  };

  // Set module-level reference for hook access
  moduleRefreshUserInfo = refreshUserInfo;

  /**
   * Handle 401 errors - attempt token refresh or logout
   */
  const handle401Error = async (): Promise<{ logout: boolean; redirectTo?: string }> => {
    // Attempt token refresh before logging out
    const newTokens = await performTokenRefresh();
    if (newTokens) {
      // Token refreshed successfully, don't log out
      return { logout: false };
    }

    // Token refresh failed - clear auth state and redirect to login
    clearTokens();
    cachedUserData = null;
    if (onTokenChange) {
      onTokenChange(null);
    }

    return {
      logout: true,
      redirectTo: "/login",
    };
  };

  // Set module-level reference for FhirClient access
  moduleHandle401Error = handle401Error;

  /**
   * Handle normal login flow
   */
  const handleNormalLogin = async (email: string, password: string, redirectTo?: string) => {
    const requestBody = transformLoginRequest(email, password);

    const response = await fetch(`${loginBaseUrl}${AUTH_PATHS.LOGIN}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const authError = mapToAuthError(errorData);
      return {
        success: false,
        error: {
          name: "",
          message: authError.message,
        },
        errorCode: authError.code,
      };
    }

    const data = await response.json();

    const challengeResult = handleChallengeRouting(data, email, redirectTo);
    if (challengeResult) {
      return challengeResult;
    }

    const tokens = transformLoginResponse(data);
    await handleSuccessfulAuth(data, tokens);

    return {
      success: true,
      redirectTo: redirectTo || "/",
    };
  };

  return {
    login: async ({
      email,
      password,
      isChallenge,
      challengeType,
      redirectTo,
    }: {
      email: string;
      password: string;
      isChallenge?: boolean;
      challengeType?: string;
      redirectTo?: string;
    }) => {
      try {
        if (isChallenge && challengeType) {
          return await handleChallengeResponse(challengeType, email, password, redirectTo);
        }
        return await handleNormalLogin(email, password, redirectTo);
      } catch (error) {
        const authError = mapToAuthError(error);
        console.error("Login error:", authError);
        return {
          success: false,
          error: {
            name: "",
            message: authError.message,
          },
          errorCode: authError.code,
        };
      }
    },

    logout: async () => {
      const redirectTo = "/login";
      try {
        // Call sign-out endpoint to revoke refresh token and clear server-side cookie
        const token = getAccessToken();
        await fetch(`${loginBaseUrl}${AUTH_PATHS.SIGN_OUT}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        }).catch(error => {
          console.error("Sign-out error:", error);
          // Continue with local logout even if server sign-out fails
        });

        // Clear stored tokens and cached user data
        clearTokens();
        cachedUserData = null;
        sessionStorage.clear();

        // Notify token change callback
        if (onTokenChange) {
          onTokenChange(null);
        }

        return {
          success: true,
          redirectTo,
        };
      } catch (error) {
        console.error("Logout error:", error);
        // Force local logout even on error
        clearTokens();
        cachedUserData = null;
        sessionStorage.clear();
        if (onTokenChange) {
          onTokenChange(null);
        }

        return {
          success: true,
          redirectTo,
        };
      }
    },

    check: async () => {
      try {
        const tokens = getStoredTokens();

        // If no tokens in memory (e.g., after page refresh), try to restore from refresh cookie
        if (!tokens) {
          const newTokens = await performTokenRefresh();
          if (newTokens) {
            return {
              authenticated: true,
            };
          }
          return {
            authenticated: false,
            redirectTo: "/login",
            logout: true,
          };
        }

        // Proactively refresh if expiring soon
        if (isTokenExpiringSoon(tokens)) {
          const newTokens = await performTokenRefresh();
          if (newTokens) {
            return {
              authenticated: true,
            };
          }
          // Refresh failed but token might still be valid
        }

        // Check if token is actually expired
        if (isTokenExpired(tokens)) {
          // Attempt token refresh
          const newTokens = await performTokenRefresh();
          if (newTokens) {
            return {
              authenticated: true,
            };
          }

          // Token expired and refresh failed
          clearTokens();
          cachedUserData = null;
          if (onTokenChange) {
            onTokenChange(null);
          }

          return {
            authenticated: false,
            redirectTo: "/login",
            logout: true,
          };
        }

        return {
          authenticated: true,
        };
      } catch (error) {
        console.error("Auth check error:", error);
        return {
          authenticated: false,
          redirectTo: "/login",
          logout: true,
        };
      }
    },

    getPermissions: async () => {
      // Implement based on your permission system
      // Could fetch from JWT, or from FHIR PractitionerRole resources
      return null;
    },

    getIdentity: async () => {
      try {
        let tokens = getStoredTokens();

        // If no tokens in memory (e.g., after page refresh), try to restore from refresh cookie
        if (!tokens) {
          tokens = await performTokenRefresh();
          if (!tokens) {
            return null;
          }
        }

        // Check if we have cached user data (populated by GraphQL refresh)
        if (!cachedUserData) {
          console.warn("No user data available after token refresh");
          return null;
        }

        // Always fetch fresh user details from GraphQL
        // React Query handles caching at the query level, so we don't need
        // to cache here. This ensures refetch() always gets fresh data.
        try {
          await refreshUserInfo();
        } catch (error) {
          console.error("Failed to fetch full user details:", error);
          // Continue with partial/cached data (at least we have user_id)
        }

        // Return identity from cached data (now refreshed)
        return {
          id: cachedUserData.user_id,
          account_id: cachedUserData.account_id,
          name: `${cachedUserData.first_name || ""} ${cachedUserData.last_name || ""}`.trim() || "Unknown User",
          firstName: cachedUserData.first_name,
          lastName: cachedUserData.last_name,
          email: cachedUserData.email,
          avatar: cachedUserData.profile_picture,
          roles: cachedUserData.roles?.map(r => r.role_name),
          mfa: cachedUserData.mfa,
          toc_completed: cachedUserData.toc_completed,
          pn_completed: cachedUserData.pn_completed,
          fhirPractitionerId: cachedUserData.fhir_practitioner_id,
        };
      } catch (error) {
        console.error("Error fetching user identity:", error);
        return null;
      }
    },

    onError: async error => {
      if (error?.status === 401 || error?.statusCode === 401) {
        // Attempt token refresh before logging out
        const newTokens = await performTokenRefresh();
        if (newTokens) {
          // Token refreshed successfully, don't log out
          return {};
        }

        clearTokens();
        cachedUserData = null;
        if (onTokenChange) {
          onTokenChange(null);
        }

        return {
          logout: true,
          redirectTo: "/login",
          error: {
            name: "AuthenticationError",
            message: "Your session has expired. Please login again.",
          },
        };
      }

      return {};
    },

    forgotPassword: async ({ email }: { email: string }) => {
      return postAuthRequest(
        AUTH_PATHS.FORGOT_PASSWORD,
        { email },
        `/submit-reset-password?email=${encodeURIComponent(email)}`
      );
    },

    updatePassword: async ({
      password,
      verificationCode,
      email,
    }: {
      password: string;
      verificationCode: string;
      email: string;
    }) => {
      if (!email) {
        return {
          success: false,
          error: {
            name: "MissingEmail",
            message: "Email is required. Please start over.",
          },
        };
      }

      return postAuthRequest(AUTH_PATHS.CONFIRM_PASSWORD, { email, newPassword: password, verificationCode }, "/login");
    },

    refreshUserInfo,
  };
};
